import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { queryClientConfig } from "@/lib/react-query-config";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import React, { Suspense } from "react";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PushBootstrap } from "@/components/push/PushBootstrap";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { DiligenciasManagerLayout } from "@/components/layout/DiligenciasManagerLayout";
import DiligenciasManagerDiligencias from "@/pages/diligencias-manager/Diligencias";
import DiligenciasManagerDiligenciaDetalhes from "@/pages/diligencias-manager/DiligenciaDetalhes";
import { AssessorLayout } from "@/components/layout/AssessorLayout";
import { InvestorLayout } from "@/components/layout/InvestorLayout";
import { PartnerLayout } from "@/components/layout/PartnerLayout";
import { IncorporadorLayout } from "@/components/layout/IncorporadorLayout";
import { RoleBasedLayout } from "@/components/layout/RoleBasedLayout";
import { LumaSpin } from "@/components/ui/luma-spin";

// Lazy load pages for better performance
const Auth = React.lazy(() => import("./pages/Auth"));
const PinLogin = React.lazy(() => import("./pages/PinLogin"));
const PinCreate = React.lazy(() => import("./pages/PinCreate"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard").then(module => ({ default: module.AdminDashboard })));
// Removed unused AssessorDashboard, use AssessorPortfolio as dashboard entry
const AssessorPortfolio = React.lazy(() => import("./pages/assessor/AssessorPortfolio").then(module => ({ default: module.AssessorPortfolio })));
const AssessorCarteira = React.lazy(() => import("./pages/assessor/AssessorCarteira").then(module => ({ default: module.AssessorCarteira })));
const AssessorInvestorDetails = React.lazy(() => import("./pages/assessor/AssessorInvestorDetails").then(module => ({ default: module.AssessorInvestorDetails })));
const AssessorInvestmentDetails = React.lazy(() => import("./pages/assessor/AssessorInvestmentDetails").then(module => ({ default: module.AssessorInvestmentDetails })));
const AssessorInvestmentInvestorDetails = React.lazy(() => import("./pages/assessor/AssessorInvestmentInvestorDetails").then(module => ({ default: module.AssessorInvestmentInvestorDetails })));
const AssessorDocumentos = React.lazy(() => import("./pages/assessor/AssessorDocumentos").then(module => ({ default: module.AssessorDocumentos })));
const InvestorDashboard = React.lazy(() => import("./pages/investor/InvestorDashboard").then(module => ({ default: module.InvestorDashboard })));
const InvestorInicio = React.lazy(() => import("./pages/investor/Inicio").then(module => ({ default: module.Inicio })));
const PartnerDashboard = React.lazy(() => import("./pages/partner/PartnerDashboard").then(module => ({ default: module.PartnerDashboard })));
const PartnerInvestments = React.lazy(() => import("./pages/partner/PartnerInvestments").then(module => ({ default: module.PartnerInvestments })));
const PartnerCarteira = React.lazy(() => import("./pages/partner/PartnerCarteira"));
const PartnerAssessorDetails = React.lazy(() => import("./pages/partner/PartnerAssessorDetails"));
const PartnerInvestorDetails = React.lazy(() => import("./pages/partner/PartnerInvestorDetails").then(module => ({ default: module.PartnerInvestorDetails })));
const PartnerInvestmentDetails = React.lazy(() => import("./pages/partner/PartnerInvestmentDetails").then(module => ({ default: module.PartnerInvestmentDetails })));
// MFB Pages
const MFBDashboard = React.lazy(() => import("./pages/mfb/MFBDashboard"));
const MFBCarteira = React.lazy(() => import("./pages/mfb/MFBCarteira"));
const MFBInvestmentDetails = React.lazy(() => import("./pages/mfb/MFBInvestmentDetails"));
const MFBPrivateInvestmentDetails = React.lazy(() => import("./pages/mfb/MFBPrivateInvestmentDetails").then(module => ({ default: module.MFBPrivateInvestmentDetails })));
const MFBAtivos = React.lazy(() => import("./pages/mfb/MFBAtivos"));
const MFBEscritorioDetails = React.lazy(() => import("./pages/mfb/MFBEscritorioDetails"));
const MFBAssessorInvestmentDetails = React.lazy(() => import("./pages/mfb/MFBAssessorInvestmentDetails"));

// MFA Pages
const MFADashboard = React.lazy(() => import("./pages/mfa/MFADashboard"));
const MFACarteira = React.lazy(() => import("./pages/mfa/MFACarteira"));
const MFAInvestmentDetails = React.lazy(() => import("./pages/mfa/MFAInvestmentDetails"));
const MFAMFBDetails = React.lazy(() => import("./pages/mfa/MFAMFBDetails"));
const MFAEscritorioDetails = React.lazy(() => import("./pages/mfa/MFAEscritorioDetails"));
const MFAAtivos = React.lazy(() => import("./pages/mfa/MFAAtivos"));
const MFAAssessorInvestmentDetails = React.lazy(() => import("./pages/mfa/MFAAssessorInvestmentDetails"));
const MyInvestments = React.lazy(() => import("./pages/investor/MyInvestments").then(module => ({ default: module.MyInvestments })));
const InvestmentDetails = React.lazy(() => import("./pages/investor/InvestmentDetails").then(module => ({ default: module.InvestmentDetails })));
const AvailableInvestments = React.lazy(() => import("./pages/investor/AvailableInvestments").then(module => ({ default: module.AvailableInvestments })));
const Comunicados = React.lazy(() => import("./pages/investor/Comunicados").then(module => ({ default: module.Comunicados })));
const ComunicadosAdmin = React.lazy(() => import("./pages/admin/Comunicados").then(module => ({ default: module.ComunicadosAdmin })));
const AdminNotificacoes = React.lazy(() => import("./pages/admin/Notificacoes").then(module => ({ default: module.AdminNotificacoes })));
const ComunicadosGeneral = React.lazy(() => import("./pages/Comunicados").then(module => ({ default: module.Comunicados })));
const PartnerDocumentos = React.lazy(() => import("./pages/partner/PartnerDocumentos").then(module => ({ default: module.PartnerDocumentos })));
const Relatorios = React.lazy(() => import("./pages/investor/Relatorios").then(module => ({ default: module.Relatorios })));
const Suporte = React.lazy(() => import("./pages/investor/Suporte").then(module => ({ default: module.Suporte })));
// TEMP: tela Beneficios desativada temporariamente — reativar removendo este comentário e a rota abaixo
// const Benefits = React.lazy(() => import("./pages/investor/Benefits").then(module => ({ default: module.Benefits })));
const Profile = React.lazy(() => import("./pages/investor/Profile"));
const Extrato = React.lazy(() => import("./pages/investor/Extrato").then(module => ({ default: module.Extrato })));
const InvestorCarteira = React.lazy(() => import("./pages/investor/Carteira"));
const WalletTopUpReturn = React.lazy(() => import("./pages/investor/WalletTopUpReturn"));
const Investimentos = React.lazy(() => import("./pages/Investimentos").then(module => ({ default: module.Investimentos })));
const Usuarios = React.lazy(() => import("./pages/Usuarios").then(module => ({ default: module.Usuarios })));
const AportesTrade = React.lazy(() => import("./pages/admin/AportesTrade"));
const AportesSCP = React.lazy(() => import("./pages/admin/AportesSCP"));
const INCCUpdates = React.lazy(() => import("./pages/admin/INCCUpdates"));
const SCPDistributions = React.lazy(() => import("./pages/admin/SCPDistributions"));
const GerenciarDocumentos = React.lazy(() => import("./pages/admin/GerenciarDocumentos"));
const GerenciarDiligencias = React.lazy(() => import("./pages/admin/GerenciarDiligencias"));
const AdminDocumentosDiligencia = React.lazy(() => import("./pages/admin/AdminDocumentosDiligencia"));
const AdminDiligenciaDetalhes = React.lazy(() => import("./pages/admin/AdminDiligenciaDetalhes"));
const AdminTermsAcceptances = React.lazy(() => import("./pages/admin/AdminTermsAcceptances"));
const AdminTermos = React.lazy(() => import("./pages/admin/AdminTermos"));
const AdminAccessLogs = React.lazy(() => import("./pages/admin/AdminAccessLogs"));
const AdminKycReview = React.lazy(() => import("./pages/admin/AdminKycReview"));
const AdminComplianceDashboard = React.lazy(() => import("./pages/admin/AdminComplianceDashboard"));
const AdminComplianceReports = React.lazy(() => import("./pages/admin/AdminComplianceReports"));
const AdminInvestorCompliance = React.lazy(() => import("./pages/admin/AdminInvestorCompliance"));
const Onboarding = React.lazy(() => import("./pages/Onboarding"));
const Regulatory = React.lazy(() => import("./pages/Regulatory"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

// Incorporador Pages
const IncorporadorDashboard = React.lazy(() => import("./pages/incorporador/IncorporadorDashboard").then(module => ({ default: module.IncorporadorDashboard })));
const MinhasDiligencias = React.lazy(() => import("./pages/incorporador/MinhasDiligencias").then(module => ({ default: module.MinhasDiligencias })));
const NovaSolicitacao = React.lazy(() => import("./pages/incorporador/NovaSolicitacao").then(module => ({ default: module.NovaSolicitacao })));
const MinhasIncorporacoes = React.lazy(() => import("./pages/incorporador/MinhasIncorporacoes").then(module => ({ default: module.MinhasIncorporacoes })));
const IncorporadorSuporteContent = React.lazy(() => import("./pages/incorporador/Suporte").then(module => ({ default: module.Suporte })));
const DiligenciaDetalhes = React.lazy(() => import("./pages/incorporador/DiligenciaDetalhes").then(module => ({ default: module.DiligenciaDetalhes })));
const CadastroIncorporadora = React.lazy(() => import("./pages/incorporador/CadastroIncorporadora"));
const OnboardingIncorporador = React.lazy(() => import("./pages/incorporador/OnboardingIncorporador"));
const ConfiguracoesNotificacoes = React.lazy(() => import("./pages/incorporador/ConfiguracoesNotificacoes").then(module => ({ default: module.ConfiguracoesNotificacoes })));

// Shared pages - accessible by all authenticated users
const SharedGeneralProducts = React.lazy(() => import("./pages/shared/GeneralProducts").then(module => ({ default: module.GeneralProducts })));
const SharedProductDetails = React.lazy(() => import("./pages/shared/ProductDetails").then(module => ({ default: module.ProductDetails })));
const EntityListPage = React.lazy(() => import("./pages/shared/EntityListPage"));

const PartnerPanelLayout: React.FC = () => (
  <ProtectedRoute requiredRole="partner">
    <ErrorBoundary>
      <PartnerLayout>
        <Outlet />
      </PartnerLayout>
    </ErrorBoundary>
  </ProtectedRoute>
);

const MFBPanelLayout: React.FC = () => (
  <ProtectedRoute requiredRole="mfb">
    <ErrorBoundary>
      <PartnerLayout>
        <Outlet />
      </PartnerLayout>
    </ErrorBoundary>
  </ProtectedRoute>
);

const MFAPanelLayout: React.FC = () => (
  <ProtectedRoute requiredRole="mfa">
    <ErrorBoundary>
      <PartnerLayout>
        <Outlet />
      </PartnerLayout>
    </ErrorBoundary>
  </ProtectedRoute>
);

// Loading component for suspense
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LumaSpin size="lg" />
  </div>
);

const queryClient = new QueryClient(queryClientConfig);


const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Sonner />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <PushBootstrap />
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Public onboarding routes */}
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/regulatory" element={<Regulatory />} />

              {/* Auth as homepage */}
              <Route path="/" element={<PinLogin />} />
              <Route path="/login-classico" element={<Auth />} />
              <Route path="/criar-pin" element={<PinCreate />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Admin Routes */}
              <Route path="/admin/*" element={
                <ProtectedRoute requiredRole="admin">
                  <ErrorBoundary>
                  <AdminLayout>
                    <Routes>
                      <Route path="dashboard" element={<AdminDashboard />} />
                      <Route path="investments" element={<Investimentos />} />
                      <Route path="aportes-trade" element={<AportesTrade />} />
                      <Route path="aportes-Private" element={<AportesSCP />} />
                      <Route path="incc" element={<INCCUpdates />} />
                      <Route path="distribuicoes-Private" element={<SCPDistributions />} />
                      <Route path="users" element={<Usuarios />} />
                      <Route path="comunicados" element={<ComunicadosAdmin />} />
                      <Route path="notificacoes" element={<AdminNotificacoes />} />
                      <Route path="gerenciar-documentos" element={<GerenciarDocumentos />} />
                      <Route path="diligencias" element={<GerenciarDiligencias />} />
                      <Route path="documentos-diligencia" element={<AdminDocumentosDiligencia />} />
                      <Route path="diligencias/:id" element={<AdminDiligenciaDetalhes />} />
                      <Route path="termos" element={<AdminTermos />} />
                      <Route path="termos-aceites" element={<Navigate to="/admin/termos?tab=aceites" replace />} />
                      <Route path="historico-acessos" element={<AdminAccessLogs />} />
                      <Route path="kyc-review" element={<AdminKycReview />} />
                      <Route path="compliance" element={<AdminComplianceDashboard />} />
                      <Route path="compliance/reports" element={<AdminComplianceReports />} />
                      <Route path="compliance/investors" element={<AdminInvestorCompliance />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="configuracoes/notificacoes" element={<ConfiguracoesNotificacoes />} />
                      <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                    </Routes>
                  </AdminLayout>
                  </ErrorBoundary>
                </ProtectedRoute>
              } />

              {/* Diligências Manager Routes (escopo restrito a diligências) */}
              <Route path="/diligencias-manager/*" element={
                <ProtectedRoute requiredRole="diligencias_manager">
                  <ErrorBoundary>
                  <DiligenciasManagerLayout>
                    <Routes>
                      <Route index element={<Navigate to="diligencias" replace />} />
                      <Route path="diligencias" element={<DiligenciasManagerDiligencias />} />
                      <Route path="diligencias/:id" element={<DiligenciasManagerDiligenciaDetalhes />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="*" element={<Navigate to="/diligencias-manager/diligencias" replace />} />
                    </Routes>
                  </DiligenciasManagerLayout>
                  </ErrorBoundary>
                </ProtectedRoute>
              } />

              {/* Assessor Routes */}
              <Route path="/assessor/*" element={
                <ProtectedRoute requiredRole="assessor">
                  <ErrorBoundary>
                  <AssessorLayout>
                    <Routes>
                      <Route path="dashboard" element={<AssessorPortfolio />} />
                      <Route path="carteira" element={<AssessorCarteira />} />
                      <Route path="list/:entityType" element={<EntityListPage />} />
                      <Route path="investor/:id" element={<AssessorInvestorDetails />} />
                      <Route path="investment/:id" element={<AssessorInvestmentDetails />} />
                      <Route path="investment/:id/investor/:investorId" element={<AssessorInvestmentInvestorDetails />} />
                      <Route path="products" element={<Navigate to="/products" replace />} />
                      <Route path="comunicados" element={<ComunicadosGeneral />} />
                      <Route path="documentos" element={<AssessorDocumentos />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="configuracoes/notificacoes" element={<ConfiguracoesNotificacoes />} />
                      <Route path="*" element={<Navigate to="/assessor/dashboard" replace />} />
                    </Routes>
                  </AssessorLayout>
                  </ErrorBoundary>
                </ProtectedRoute>
              } />

              {/* Partner Routes */}
              <Route path="/partner" element={<PartnerPanelLayout />}>
                <Route path="dashboard" element={<PartnerDashboard />} />
                <Route path="carteira" element={<PartnerCarteira />} />
                <Route path="list/:entityType" element={<EntityListPage />} />
                <Route path="assessor/:id" element={<PartnerAssessorDetails />} />
                <Route path="investor/:investorId" element={<PartnerInvestorDetails />} />
                <Route path="investor/:investorId/investment/:investmentId" element={<AssessorInvestmentInvestorDetails />} />
                <Route path="investment/:id" element={<PartnerInvestmentDetails />} />
                <Route path="products" element={<Navigate to="/products" replace />} />
                <Route path="investments" element={<PartnerInvestments />} />
                <Route path="comunicados" element={<ComunicadosGeneral />} />
                <Route path="documentos" element={<PartnerDocumentos />} />
                <Route path="profile" element={<Profile />} />
                <Route path="configuracoes/notificacoes" element={<ConfiguracoesNotificacoes />} />
                <Route path="*" element={<Navigate to="/partner/dashboard" replace />} />
              </Route>

              {/* Escritório Routes (NOVO) */}
              <Route path="/escritorio" element={<PartnerPanelLayout />}>
                <Route path="dashboard" element={<PartnerDashboard />} />
                <Route path="carteira" element={<PartnerCarteira />} />
                <Route path="list/:entityType" element={<EntityListPage />} />
                <Route path="assessor/:id" element={<PartnerAssessorDetails />} />
                <Route path="investor/:investorId" element={<PartnerInvestorDetails />} />
                <Route path="investor/:investorId/investment/:investmentId" element={<AssessorInvestmentInvestorDetails />} />
                <Route path="investment/:id" element={<PartnerInvestmentDetails />} />
                <Route path="products" element={<Navigate to="/products" replace />} />
                <Route path="investments" element={<PartnerInvestments />} />
                <Route path="comunicados" element={<ComunicadosGeneral />} />
                <Route path="documentos" element={<PartnerDocumentos />} />
                <Route path="profile" element={<Profile />} />
                <Route path="configuracoes/notificacoes" element={<ConfiguracoesNotificacoes />} />
                <Route path="*" element={<Navigate to="/escritorio/dashboard" replace />} />
              </Route>

              {/* MFB Routes */}
              <Route path="/mfb" element={<MFBPanelLayout />}>
                <Route path="dashboard" element={<MFBDashboard />} />
                <Route path="carteira" element={<MFBCarteira />} />
                <Route path="list/:entityType" element={<EntityListPage />} />
                <Route path="assessor/:id" element={<PartnerAssessorDetails />} />
                <Route path="assessor/:assessorId/investment/:investmentId" element={<MFBAssessorInvestmentDetails />} />
                <Route path="escritorio/:id" element={<MFBEscritorioDetails />} />
                <Route path="investor/:investorId" element={<PartnerInvestorDetails />} />
                <Route path="investor/:investorId/investment/:investmentId" element={<AssessorInvestmentInvestorDetails />} />
                <Route path="ativos" element={<MFBAtivos />} />
                <Route path="investment/:id/details" element={<MFBPrivateInvestmentDetails />} />
                <Route path="investment/:id" element={<MFBInvestmentDetails />} />
                <Route path="products" element={<Navigate to="/products" replace />} />
                <Route path="investments" element={<PartnerInvestments />} />
                <Route path="comunicados" element={<ComunicadosGeneral />} />
                <Route path="documentos" element={<PartnerDocumentos />} />
                <Route path="profile" element={<Profile />} />
                <Route path="configuracoes/notificacoes" element={<ConfiguracoesNotificacoes />} />
                <Route path="*" element={<Navigate to="/mfb/dashboard" replace />} />
              </Route>

              {/* MFA Routes */}
              <Route path="/mfa" element={<MFAPanelLayout />}>
                <Route path="dashboard" element={<MFADashboard />} />
                <Route path="carteira" element={<MFACarteira />} />
                <Route path="list/:entityType" element={<EntityListPage />} />
                <Route path="assessor/:id" element={<PartnerAssessorDetails />} />
                <Route path="assessor/:assessorId/investment/:investmentId" element={<MFAAssessorInvestmentDetails />} />
                <Route path="escritorio/:id" element={<MFAEscritorioDetails />} />
                <Route path="mfb/:id" element={<MFAMFBDetails />} />
                <Route path="investor/:investorId" element={<PartnerInvestorDetails />} />
                <Route path="investor/:investorId/investment/:investmentId" element={<AssessorInvestmentInvestorDetails />} />
                <Route path="ativos" element={<MFAAtivos />} />
                <Route path="investment/:id" element={<MFAInvestmentDetails />} />
                <Route path="products" element={<Navigate to="/products" replace />} />
                <Route path="investments" element={<PartnerInvestments />} />
                <Route path="comunicados" element={<ComunicadosGeneral />} />
                <Route path="documentos" element={<PartnerDocumentos />} />
                <Route path="profile" element={<Profile />} />
                <Route path="configuracoes/notificacoes" element={<ConfiguracoesNotificacoes />} />
                <Route path="*" element={<Navigate to="/mfa/dashboard" replace />} />
              </Route>

              {/* Investor Routes */}
              <Route path="/investor/*" element={
                <ProtectedRoute requiredRole="investidor">
                  <ErrorBoundary>
                  <InvestorLayout>
                    <Routes>
                      <Route path="inicio" element={<InvestorInicio />} />
                      <Route path="dashboard" element={<InvestorDashboard />} />
                      <Route path="my-investments" element={<MyInvestments />} />
                      <Route path="investments/:id" element={<InvestmentDetails />} />
                      <Route path="products" element={<Navigate to="/products" replace />} />
                      <Route path="available-investments" element={<AvailableInvestments />} />
                      <Route path="products/:id/details" element={<Navigate to="/products/:id/details" replace />} />
                      <Route path="comunicados" element={<Comunicados />} />
                      <Route path="relatorios" element={<Relatorios />} />
                      <Route path="extratos" element={<Extrato />} />
                      {/* TEMP: rota Beneficios desativada — "*" abaixo já redireciona para dashboard */}
                      {/* <Route path="beneficios" element={<Benefits />} /> */}
                      <Route path="suporte" element={<Suporte />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="configuracoes/notificacoes" element={<ConfiguracoesNotificacoes />} />
                      <Route path="investments" element={<Investimentos />} />
                      <Route path="carteira" element={<InvestorCarteira />} />
                      <Route path="pagamentos" element={<Navigate to="/investor/carteira" replace />} />
                      <Route path="*" element={<Navigate to="/investor/dashboard" replace />} />
                    </Routes>
                  </InvestorLayout>
                  </ErrorBoundary>
                </ProtectedRoute>
              } />

              {/* Retorno do checkout pagar.me (top-up de carteira) */}
              <Route path="/wallet/top-up/success" element={
                <ProtectedRoute requiredRole="investidor">
                  <ErrorBoundary>
                    <WalletTopUpReturn status="success" />
                  </ErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path="/wallet/top-up/cancel" element={
                <ProtectedRoute requiredRole="investidor">
                  <ErrorBoundary>
                    <WalletTopUpReturn status="cancel" />
                  </ErrorBoundary>
                </ProtectedRoute>
              } />

              {/* Incorporador Onboarding (tela cheia, sem layout) */}
              <Route path="/incorporador/onboarding" element={
                <ProtectedRoute requiredRole="incorporador">
                  <ErrorBoundary>
                    <OnboardingIncorporador />
                  </ErrorBoundary>
                </ProtectedRoute>
              } />

              {/* Incorporador Routes */}
              <Route path="/incorporador/*" element={
                <ProtectedRoute requiredRole="incorporador">
                  <ErrorBoundary>
                  <IncorporadorLayout>
                    <Routes>
                      <Route path="dashboard" element={<IncorporadorDashboard />} />
                      <Route path="diligencias" element={<MinhasDiligencias />} />
                      <Route path="diligencias/:id" element={<DiligenciaDetalhes />} />
                      <Route path="nova-solicitacao" element={<NovaSolicitacao />} />
                      <Route path="incorporacoes" element={<MinhasIncorporacoes />} />
                      <Route path="suporte" element={<IncorporadorSuporteContent />} />
                      <Route path="profile" element={<CadastroIncorporadora />} />
                      <Route path="configuracoes/notificacoes" element={<ConfiguracoesNotificacoes />} />
                      <Route path="*" element={<Navigate to="/incorporador/dashboard" replace />} />
                    </Routes>
                  </IncorporadorLayout>
                  </ErrorBoundary>
                </ProtectedRoute>
              } />

              {/* Shared Routes - Accessible by all authenticated users */}
              <Route path="/products" element={
                <ProtectedRoute allowedRoles={['admin', 'partner', 'assessor', 'investidor', 'mfb', 'mfa', 'incorporador']}>
                  <RoleBasedLayout>
                    <SharedGeneralProducts />
                  </RoleBasedLayout>
                </ProtectedRoute>
              } />
              <Route path="/products/:id/details" element={
                <ProtectedRoute allowedRoles={['admin', 'partner', 'assessor', 'investidor', 'mfb', 'mfa', 'incorporador']}>
                  <RoleBasedLayout>
                    <SharedProductDetails />
                  </RoleBasedLayout>
                </ProtectedRoute>
              } />

              {/* Legacy product routes - redirect to shared routes */}
              <Route path="/assessor/products" element={<Navigate to="/products" replace />} />
              <Route path="/assessor/products/:id/details" element={<Navigate to="/products/:id/details" replace />} />
              <Route path="/partner/products" element={<Navigate to="/products" replace />} />
              <Route path="/partner/products/:id/details" element={<Navigate to="/products/:id/details" replace />} />

              {/* Legacy routes - redirect to role-based routes */}
              <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/investimentos" element={<Navigate to="/admin/investments" replace />} />
              <Route path="/usuarios" element={<Navigate to="/admin/users" replace />} />
              
              {/* Redirect any other path to auth */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;

